import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Plus, Trash, Download, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { pdf } from "@react-pdf/renderer";
import ReactPdfOrcamento from "./ReactPdfOrcamento";

const OrcamentoModal = ({ isOpen, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      empresa: "",
      cnpj: "",
      endereco: "",
      tipoPacote: "2",
      dataValidade: "",
      atendimentoSabado: false,
      solicitouSobremesa: false,
      itens: [{ descricao: "", qtd: 1, valorUnitario: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "itens",
  });

  const handleGeneratePdf = async (data) => {
    try {
      setIsGenerating(true);
      toast.loading("Gerando PDF...", { id: "pdf-toast" });

      // Instanciar o documento PDF na memória usando o @react-pdf/renderer
      const blob = await pdf(<ReactPdfOrcamento data={data} />).toBlob();

      // Criar URL para o arquivo local e forçar o download automático
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `orcamento-${data.empresa.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Limpeza de memória
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("PDF gerado com sucesso!", { id: "pdf-toast" });
      onClose();
    } catch (error) {
      console.error("Erro ao gerar PDF", error);
      toast.error("Erro ao gerar PDF", { id: "pdf-toast" });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[90vh] sm:max-w-[700px] p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
          <DialogHeader className="px-6 py-4 border-b border-slate-100 dark:border-border shrink-0">
            <DialogTitle>Criar Orçamento</DialogTitle>
          </DialogHeader>

          <div className="px-6 py-6 overflow-y-auto flex-1">
            <form
              id="orcamento-form"
              onSubmit={handleSubmit(handleGeneratePdf)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Dados do Solicitante
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="empresa">
                    Empresa / Instituição{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="empresa"
                    {...register("empresa", { required: "Campo obrigatório" })}
                    placeholder="Nome da empresa ou instituição"
                  />
                  {errors.empresa && (
                    <p className="text-xs text-red-500">
                      {errors.empresa.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cnpj">CNPJ</Label>
                    <Input
                      id="cnpj"
                      {...register("cnpj")}
                      placeholder="00.000.000/0001-00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dataValidade">
                      Data de Validade <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="dataValidade"
                      type="date"
                      {...register("dataValidade", {
                        required: "Campo obrigatório",
                      })}
                      className="force-light-datepicker w-fit min-w-[160px]"
                    />
                    {errors.dataValidade && (
                      <p className="text-xs text-red-500">
                        {errors.dataValidade.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input
                    id="endereco"
                    {...register("endereco")}
                    placeholder="Endereço completo"
                  />
                </div>

                <div className="flex flex-col space-y-3 pt-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="atendimentoSabado"
                      {...register("atendimentoSabado")}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label
                      htmlFor="atendimentoSabado"
                      className="font-normal cursor-pointer"
                    >
                      Atendimento apenas aos sábados
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="solicitouSobremesa"
                      {...register("solicitouSobremesa")}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label
                      htmlFor="solicitouSobremesa"
                      className="font-normal cursor-pointer"
                    >
                      Solicitou sobremesa
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Opções de Pacote (Referência)
                </h3>
                <div className="space-y-3 border p-4 rounded-md">
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id="pacote1"
                      value="1"
                      {...register("tipoPacote")}
                      className="mt-1"
                    />
                    <Label
                      htmlFor="pacote1"
                      className="font-normal cursor-pointer"
                    >
                      1. Apenas entradas. <br />
                      <span className="text-xs text-muted-foreground">
                        REF.: DIA DE LAZER, INCLUINDO ENTRADAS NOS DOIS PARQUEs
                        AQUÁTICOS.
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id="pacote2"
                      value="2"
                      {...register("tipoPacote")}
                      className="mt-1"
                    />
                    <Label
                      htmlFor="pacote2"
                      className="font-normal cursor-pointer"
                    >
                      2. Incluir entradas + almoço e bebidas. <br />
                      <span className="text-xs text-muted-foreground">
                        REF.: DIA DE LAZER, INCLUINDO ENTRADAS NOS DOIS PARQUEs
                        AQUÁTICOS + ALMOÇO + REFRI OU SUCO OU ÁGUA.
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      id="pacote3"
                      value="3"
                      {...register("tipoPacote")}
                      className="mt-1"
                    />
                    <Label
                      htmlFor="pacote3"
                      className="font-normal cursor-pointer"
                    >
                      3. Entradas + almoço. <br />
                      <span className="text-xs text-muted-foreground">
                        REF.: DIA DE LAZER, INCLUINDO ENTRADAS NOS DOIS PARQUEs
                        AQUÁTICOS + ALMOÇO.
                      </span>
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Itens do Orçamento
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      append({ descricao: "", qtd: 1, valorUnitario: 0 })
                    }
                  >
                    <Plus className="w-4 h-4 mr-2" /> Adicionar Item
                  </Button>
                </div>

                <div className="space-y-3">
                  {fields.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex flex-col sm:flex-row gap-3 items-start border p-3 rounded-md bg-slate-50 dark:bg-card"
                    >
                      <div className="w-full sm:flex-1 space-y-2">
                        <Label className="text-xs">Descrição do Item</Label>
                        <Input
                          {...register(`itens.${index}.descricao`, {
                            required: true,
                          })}
                          placeholder="Ex: ENTRADA PARA OS DOIS PARQUES"
                        />
                      </div>

                      <div className="flex w-full sm:w-auto gap-2 items-start">
                        <div className="flex-1 sm:w-24 space-y-2">
                          <Label className="text-xs">Qtd</Label>
                          <Input
                            type="number"
                            {...register(`itens.${index}.qtd`, {
                              required: true,
                              min: 1,
                            })}
                            min="1"
                          />
                        </div>
                        <div className="flex-1 sm:w-32 space-y-2">
                          <Label className="text-xs">Valor Unit. (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            {...register(`itens.${index}.valorUnitario`, {
                              required: true,
                              min: 0,
                            })}
                            min="0"
                          />
                        </div>
                        <div className="pt-6">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700 mt-1"
                            onClick={() => remove(index)}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-border bg-slate-50 dark:bg-card shrink-0">
            <Button variant="outline" onClick={onClose} disabled={isGenerating} className="w-full sm:w-auto min-h-[44px]">
              Cancelar
            </Button>
            <Button type="submit" form="orcamento-form" disabled={isGenerating} className="w-full sm:w-auto min-h-[44px]">
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" /> Gerar PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrcamentoModal;
